import { ChangeEvent, FC, MouseEvent, useEffect, useState } from 'react';
import { NumberFormatValues, SourceInfo } from 'react-number-format';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { Buttons, Form, FormElements } from '../../../common/AddEditFormLayout/AddEditFormLayout.styles';
import {
  DropdownWrapperLarge,
  DropdownWrapperMedium,
  InputWrapperLarge,
  InputWrapperMedium
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import MaskedInput from '../../../components/MaskedInput/MaskedInput';
import RadioGroup from '../../../components/RadioGroup/RadioGroup';
import { RadioGroupLayouts } from '../../../components/RadioGroup/RadioGroup.types';
import { STATUS, StatusKeys } from '../../../constants/ApiEnums';
import { useEditOption, useOption } from '../../../services/option/option.service';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import { Row } from './EditOption.styles';
import { HorizontalLine, SubTitle } from '../../Classes/ClassForm/ClassForm.styles';
import ImageForm from '../../../components/ImageForm/ImageForm';
import { readOnlyLabelTableText } from '../../../constants/GlobalTexts';

const EditOption: FC = () => {
  const { classNo, optionNo } = useParams();
  const navigateWithToast = useNavigateWithToast();
  const queryClient = useQueryClient();
  const _optionNo = optionNo as string;
  const _classNo = classNo as string;

  const { data, isError, refetch } = useOption({ optionNo: _optionNo, classNo: _classNo }, true);

  const { mutate, isError: editIsError, isLoading, error: editError } = useEditOption(optionNo as string);

  useFetchErrorToast([{ isError, label: 'Error fetching option data', id: 'optionDataFetchError' }]);

  useGenericErrorMessage([{ isError: editIsError, error: editError }]);

  const optionData = data?.data;

  const [formValues, setFormValues] = useState({
    makeSeq: '',
    toppingSubstitutionNo: '',
    displayInOrderEntry: false,
    validOnWeb: false,
    displayDesc: '',
    isModifier: false,
    opsProcess: '',
    excludeOptionNo: '',
    sequenceNo: '', 
    statusNo: '',
    toppingTypeCode: '',
    webDisplay: '',
    toppingCategory: '',
    pricingSeq: '',
  });

  const [initialFormValues, setInitialFormValues] = useState({
    makeSeq: '',
    toppingSubstitutionNo: '',
    displayInOrderEntry: false,
    validOnWeb: false,
    displayDesc: '',
    isModifier: false,
    opsProcess: '',
    excludeOptionNo: '',
    sequenceNo: '', 
    statusNo: '',
    toppingTypeCode: '',
    webDisplay: '',
    toppingCategory: '',
    pricingSeq: '',
  });

  const [formErrors, setFormErrors] = useState({
    displayDesc: '',
    makeSeq: '',
    excludeOptionNo: '',
    sequenceNo: '',
    webDisplay: '',
    pricingSeq: '',
    toppingCategory: '',
    toppingTypeCode: '',
    opsProcess: '',
  });

  const [unsavedChangesExist, setUnsavedChangesExist] = useState(false);

  const checkForUnsavedChanges = () => {
    if (
      initialFormValues.makeSeq !== formValues.makeSeq ||
      initialFormValues.toppingSubstitutionNo !== formValues.toppingSubstitutionNo ||
      initialFormValues.displayInOrderEntry !== formValues.displayInOrderEntry ||
      initialFormValues.validOnWeb !== formValues.validOnWeb ||
      initialFormValues.displayDesc !== formValues.displayDesc ||
      initialFormValues.isModifier !== formValues.isModifier ||
      initialFormValues.opsProcess !== formValues.opsProcess ||
      initialFormValues.excludeOptionNo !== formValues.excludeOptionNo || 
      initialFormValues.sequenceNo !== formValues.sequenceNo ||
      initialFormValues.statusNo !== formValues.statusNo ||
      initialFormValues.toppingTypeCode !== formValues.toppingTypeCode ||
      initialFormValues.webDisplay !== formValues.webDisplay ||
      initialFormValues.toppingCategory !== formValues.toppingCategory ||
      initialFormValues.pricingSeq !== formValues.pricingSeq
    ) {
      setUnsavedChangesExist(true);
    } else {
      setUnsavedChangesExist(false);
    }
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [
    formValues.makeSeq,
    formValues.toppingSubstitutionNo,
    formValues.displayInOrderEntry,
    formValues.validOnWeb,
    formValues.displayDesc,
    formValues.isModifier,
    formValues.opsProcess,
    formValues.excludeOptionNo,
    formValues.sequenceNo,
    formValues.statusNo,
    formValues.toppingTypeCode,
    formValues.webDisplay,
    formValues.toppingCategory,
    formValues.pricingSeq,
    optionData,
  ]);

  useEffect(() => {
    if (optionData) {
      setFormValues({
        toppingSubstitutionNo: optionData.toppingSubstitutionNo?.toString() || '',
        makeSeq: optionData.makeSeq?.toString() || '',
        displayInOrderEntry: optionData.displayInOrderEntry,
        validOnWeb: optionData.validOnWeb,
        displayDesc: optionData.displayDesc,
        isModifier: optionData.isModifier ?? false,
        opsProcess: optionData.opsProcess || 'space',
        excludeOptionNo: optionData.excludeOptionNo?.toString() || '',
        sequenceNo: optionData.sequenceNo?.toString(),
        statusNo: optionData.statusNo?.toString(),
        toppingTypeCode: optionData.toppingTypeCode?.toString() || '0',
        webDisplay: optionData.webDisplay?.toString() || '',
        toppingCategory: optionData.toppingCategory?.toString() || 'T',
        pricingSeq: optionData.pricingSeq?.toString() || '',
      });
      setInitialFormValues({
        toppingSubstitutionNo: optionData.toppingSubstitutionNo?.toString() || '',
        makeSeq: optionData.makeSeq?.toString() || '',
        displayInOrderEntry: optionData.displayInOrderEntry,
        validOnWeb: optionData.validOnWeb,
        displayDesc: optionData.displayDesc,
        isModifier: optionData.isModifier ?? false,
        opsProcess: optionData.opsProcess || 'space',
        excludeOptionNo: optionData.excludeOptionNo?.toString() || '',
        sequenceNo: optionData.sequenceNo?.toString(),
        statusNo: optionData.statusNo?.toString(),
        toppingTypeCode: optionData.toppingTypeCode?.toString() || '0',
        webDisplay: optionData.webDisplay?.toString() || '',
        toppingCategory: optionData.toppingCategory?.toString() || 'T',
        pricingSeq: optionData.pricingSeq?.toString() || '',
      });
    }
  }, [optionData]);

  const handleMaskedInputChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (sourceInfo.event) {
      setFormValues({ ...formValues, [sourceInfo.event.target.id]: values.value === '' ? '' : values.value });
      setFormErrors({ ...formErrors, [sourceInfo.event.target.id]: '' });
    }
  };

  const handleMakeSeqChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    setFormErrors({
      ...formErrors,
      makeSeq: '',
    });
    if (sourceInfo.event) {
      setFormValues({
        ...formValues,
        [sourceInfo.event.target.id]: values.value === '' ? '' : values.value,
      });
    }
  };

  const handleDisplayDescChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormErrors({
      ...formErrors,
      displayDesc: '',
    });
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleDropdownChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleCategoryDropdownChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
    setFormErrors({ ...formErrors, [event.target.id]: '' });
  };

  const handleModifierChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, isModifier: event.target.value === 'isModifierYes' });
  };

  const handleDisplayInOrderEntryChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, displayInOrderEntry: event.target.id === 'displayInOrderEntryYes' });
  };

  const handleValidonWebChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, validOnWeb: event.target.id === 'validOnWebYes' });
  };

  const handlSequenceNoChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (sourceInfo.event) {
      setFormValues({ ...formValues, [sourceInfo.event.target.id]: values.value === '' ? '' : values.value });
      setFormErrors({ ...formErrors, [sourceInfo.event.target.id]: '' });
    }
  };

  const handleSaveClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    let error = false;
    let newFormErrors = {
      displayDesc: '',
      makeSeq: '',
      excludeOptionNo: '',
      sequenceNo: '',
      webDisplay: '',
      pricingSeq: '',
      toppingCategory: '',
      toppingTypeCode: '',
      opsProcess: '',
    };
    if (formValues.displayDesc?.length > 9) {
      error = true;
      newFormErrors.displayDesc = 'BOH Description must be 9 characters or less';
    }
    if (!formValues.displayDesc || formValues.displayDesc.length < 1) {
      error = true;
      newFormErrors.displayDesc = 'Please enter a BOH Description';
    }
    if (formValues.makeSeq.length < 1) {
      error = true;
      newFormErrors.makeSeq = 'Please enter a Make Sequence';
    }
    if (formValues.makeSeq.length > 2) {
      error = true;
      newFormErrors.makeSeq = 'Make Sequence must be 2 characters or less';
    }
    if (+formValues.excludeOptionNo > 2147483646) {
      error = true;
      newFormErrors.excludeOptionNo = 'Exclude Option Number is too long';
    }
    if ([null, undefined, -1, ''].includes(formValues.sequenceNo)) {
      error = true;
      newFormErrors.sequenceNo = 'Sequence Number is required.';
    }
    if(formValues.sequenceNo?.length > 3) {
      error = true;
      newFormErrors.sequenceNo = 'Sequence Number must be 3 characters or less'
    }
    if (+formValues.webDisplay > 2147483646) {
      error = true;
      newFormErrors.webDisplay = 'Web Display Sequence Number is too long';
    }
    if ([null, undefined, -1, ''].includes(formValues.webDisplay)) {
      error = true;
      newFormErrors.webDisplay = 'Web Display Sequence Number is required.';
    }
    if ([null, undefined, -1, ''].includes(formValues.pricingSeq)) {
      error = true;
      newFormErrors.pricingSeq = 'Price Sequence is required.';
    }
    if ([null, undefined, -1, ''].includes(formValues.toppingCategory)) {
      error = true;
      newFormErrors.toppingCategory = 'Please select a Topping Category.';
    }
    if ([null, undefined, -1, ''].includes(formValues.toppingTypeCode)) {
      error = true;
      newFormErrors.toppingCategory = 'Please select a Topping Type Code.';
    }
    if ([null, undefined, -1, ''].includes(formValues.opsProcess)) {
      error = true;
      newFormErrors.opsProcess = 'Please select a BOH Process.';
    }
    if (formValues.pricingSeq?.toString().length > 3) {
      error = true;
      newFormErrors.pricingSeq = 'Price Sequence must be 3 digits or less';
    }
    if (!error) {
      setUnsavedChangesExist(false);
      mutate(
        {
          classNo: +_classNo,
          toppingSubstitutionNo:
            formValues.toppingSubstitutionNo === 'noSelection' ? null : +formValues.toppingSubstitutionNo,
          makeSeq: +formValues.makeSeq,
          displayInOrderEntry: formValues.displayInOrderEntry,
          validOnWeb: formValues.validOnWeb,
          displayDesc: formValues.displayDesc,
          isModifier: formValues.isModifier,
          opsProcess: formValues.opsProcess === 'space' ? ' ' : formValues.opsProcess,
          excludeOptionNo: +formValues.excludeOptionNo,
          statusNo: +formValues.statusNo || 0,
          sequenceNo: +formValues.sequenceNo,
          toppingTypeCode: formValues.toppingTypeCode,
          webDisplay: +formValues.webDisplay,
          toppingCategory: formValues.toppingCategory,
          pricingSeq: formValues.pricingSeq,
        },
        {
          onSuccess: () => {
            navigateWithToast('/tables/options', {
              label: 'Option has been updated successfully.',
              id: 'editOptionSuccess',
            });
            queryClient.invalidateQueries(['options', _optionNo, _classNo]);
            setInitialFormValues({ ...formValues });
            refetch();
          },
        }
      );
    } else {
      setFormErrors(newFormErrors);
    }
  };

  return (
    <>
      <Header
        breadcrumb={{
          label: 'Back to Option Table',
          to: '/tables/options',
        }}
        title={`View Option ${optionData?.optionNo ? `- ${optionData.optionNo}` : ''}`}
      />
      <Form>
        <FormElements>
          <H2>Information</H2>
          <Row>
            <InputWrapperMedium>
              <Input label='MOA Class Number' id='moaClassNo' disabled value={optionData?.classNo?.toString()} />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input label='MOA Option Number' id='moaOptionNo' disabled value={optionData?.optionNo?.toString()} />
            </InputWrapperMedium>
          </Row>
          <Row>
            <InputWrapperMedium>
              <Input label='Option Number' id='optionNo' disabled value={optionData?.optionNo?.toString()} />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <MaskedInput
                label='Sequence Number'
                id='sequenceNo'
                value={formValues?.sequenceNo?.toString()}
                errorText={formErrors?.sequenceNo}
                isAllowed={(values) => +values.value < 2147483648}
                onValueChange={handlSequenceNoChange}
                decimalScale={0}
              />
            </InputWrapperMedium>
          </Row>
          <Row>
            <InputWrapperLarge>
              <Input label='Short Description' id='description' disabled value={optionData?.description?.toString()} />
            </InputWrapperLarge>
            <InputWrapperLarge>
              <Input
                label='Long Description'
                id='longDescription'
                disabled
                value={optionData?.longDescription?.toString()}
              />
            </InputWrapperLarge>
          </Row>
          <Row>
            <DropdownWrapperLarge>
              <Dropdown
                label='Status'
                id='statusNo'
                options={Object.keys(STATUS).map((key: any) => {
                  return { value: key, label: `${key} - ${STATUS[key as StatusKeys]}` };
                })}
                value={optionData?.statusNo?.toString()}
                onChange={handleDropdownChange}
              />
            </DropdownWrapperLarge>
            <InputWrapperMedium>
              <MaskedInput
                label='Make Sequence'
                id='makeSeq'
                value={formValues.makeSeq}
                onValueChange={handleMakeSeqChange}
                errorText={formErrors.makeSeq}
                decimalScale={0}
              />
            </InputWrapperMedium>
          </Row>
          <Row>
            <DropdownWrapperLarge>
              <Dropdown
                label='Topping Type Code'
                id='toppingTypeCode'
                options={[
                  { value: '0', label: '0 - N/A' },
                  { value: 'C', label: 'C - Cheese Toppings' },
                  { value: 'P', label: 'P - Preferences' },
                  { value: 'T', label: 'T - Normal Toppings' },
                ]}
                value={optionData?.toppingTypeCode || '0'}
                onChange={handleDropdownChange}
              />
            </DropdownWrapperLarge>
            <DropdownWrapperLarge>
              <Dropdown
                label='Topping Sub Number'
                id='toppingSubstitutionNo'
                options={
                  _classNo !== '1'
                    ? [
                        { value: 'noSelection', label: 'Select' },
                        { value: '0', label: '0 - N/A' },
                        { value: '1', label: '1 - Meat Substitutions' },
                        { value: '2', label: '2 - Vegetable Substitutions' },
                        { value: '9', label: '9 - No Substitutions Allowed' },
                      ]
                    : [
                        { value: '0', label: '0 - N/A' },
                        { value: '1', label: '1 - Meat Substitutions' },
                        { value: '2', label: '2 - Vegetable Substitutions' },
                        { value: '9', label: '9 - No Substitutions Allowed' },
                      ]
                }
                value={optionData?.toppingSubstitutionNo?.toString() || 'noSelection'}
                onChange={handleDropdownChange}
                hidePlaceholder={_classNo !== '1'}
              />
            </DropdownWrapperLarge>
          </Row>
          <Row>
            <InputWrapperMedium>
              <MaskedInput
                label='Exclude Option Number'
                id='excludeOptionNo'
                value={formValues.excludeOptionNo}
                onValueChange={handleMaskedInputChange}
                errorText={formErrors.excludeOptionNo}
                decimalScale={0}
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input label='Mnemonic' id='mnemonic' disabled value={optionData?.mnemonic?.toString()} />
            </InputWrapperMedium>
          </Row>
          <Row>
            <InputWrapperMedium>
              <RadioGroup
                label='Display In Order Entry'
                layout={RadioGroupLayouts.Row}
                id='displayInOrderEntry'
                selected={formValues.displayInOrderEntry ? 'displayInOrderEntryYes' : 'displayInOrderEntryNo'}
                options={[
                  { label: 'Yes', id: 'displayInOrderEntryYes' },
                  { label: 'No', id: 'displayInOrderEntryNo' },
                ]}
                onChange={handleDisplayInOrderEntryChange}
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <RadioGroup
                label='Valid on web'
                layout={RadioGroupLayouts.Row}
                id='validOnWeb'
                selected={optionData?.validOnWeb ? 'validOnWebYes' : 'validOnWebNo'}
                options={[
                  { label: 'Yes', id: 'validOnWebYes' },
                  { label: 'No', id: 'validOnWebNo' },
                ]}
                onChange={handleValidonWebChange}
              />
            </InputWrapperMedium>
          </Row>
          <Row>
            <InputWrapperMedium>
              <MaskedInput
                label='Web Display Sequence No.'
                id='webDisplay'
                value={optionData?.webDisplay?.toString()}
                errorText={formErrors.webDisplay}
                isAllowed={(values) => +values.value < 2147483648}
                onValueChange={handleMaskedInputChange}
              />
            </InputWrapperMedium>
            <InputWrapperLarge>
              <Input
                label='BOH Description'
                id='displayDesc'
                value={formValues.displayDesc}
                onChange={handleDisplayDescChange}
                errorText={formErrors.displayDesc}
              />
            </InputWrapperLarge>
          </Row>
          <Row>
            <DropdownWrapperMedium>
              <Dropdown
                label='BOH Process'
                id='opsProcess'
                options={[
                  { value: 'space', label: ' ' },
                  { value: 'M', label: 'M' },
                  { value: 'C', label: 'C' },
                ]}
                value={formValues.opsProcess}
                onChange={handleCategoryDropdownChange}
                errorText={formErrors.opsProcess}
              />
            </DropdownWrapperMedium>
            <DropdownWrapperMedium>
              <Dropdown
                label='BOH Modifier'
                id='isModifier'
                options={[
                  { value: 'isModifierYes', label: 'Yes' },
                  { value: 'isModifierNo', label: 'No' },
                ]}
                value={formValues?.isModifier ? 'isModifierYes' : 'isModifierNo'}
                onChange={handleModifierChange}
              />
            </DropdownWrapperMedium>
          </Row>
          <Row>
            <DropdownWrapperLarge>
              <Dropdown
                label='Topping Category'
                id='toppingCategory'
                value={optionData?.toppingCategory || ''}
                options={[
                  { value: 'T', label: 'T - Topping' },
                  { value: 'S', label: 'S - Sauce' },
                  { value: 'F', label: 'F - Finisher' },
                  { value: 'D', label: 'D - Drizzle' },
                ]}
                onChange={handleCategoryDropdownChange}
                errorText={formErrors.toppingCategory}
              />
            </DropdownWrapperLarge>
            <InputWrapperMedium>
              <MaskedInput label='Price Sequence'
                     id='pricingSeq'
                     value={optionData?.pricingSeq?.toString()}
                     onValueChange={handleMaskedInputChange}
                     errorText={formErrors.pricingSeq}
                     decimalScale={0}
                     allowNegative={false}
              />
            </InputWrapperMedium>
          </Row>
          { classNo &&
              optionNo &&
              <>
                <HorizontalLine />
                <ImageForm resourceNo={[Number(classNo), Number(optionNo)]} attribute={'topp'} />
              </>
          }
        </FormElements>
        <Buttons>
          <Button label='Save' onClick={handleSaveClick} disabled={!unsavedChangesExist} isLoading={isLoading} />
        </Buttons>
      </Form>
      <SubTitle>{readOnlyLabelTableText}</SubTitle>
      <UnsavedChangesModal unsavedChangesExist={unsavedChangesExist} />
    </>
  );
};

export default EditOption;
